import { useEffect, useState } from "react";
import { Sun, Moon, Sparkles, Star } from "lucide-react";
import { api } from "@/lib/api";
import { OmMark, DecorativeDivider } from "@/components/Ornaments";

let _cache = null;

function usePanchang() {
  const [data, setData] = useState(_cache);
  useEffect(() => {
    if (_cache) return;
    api.get("/panchang/today")
      .then(({ data }) => { _cache = data; setData(data); })
      .catch(() => setData({ enabled: false }));
  }, []);
  return data;
}

/** Subtle, premium line shown above the hero heading. */
export function PanchangBanner({ tone = "light" }) {
  const p = usePanchang();
  if (!p || !p.enabled || !p.banner_enabled) return null;

  const text = (p.override_text && p.override_text.trim()) || p.headline;
  if (!text) return null;

  const color = tone === "light"
    ? "text-[#f3d7a8] border-[#f3d7a8]/30 bg-[#1a1514]/35"
    : "text-[color:var(--brand)] border-[color:var(--brand)]/25 bg-[color:var(--bg-2)]";

  return (
    <div
      data-testid="panchang-banner"
      className={`inline-flex items-center gap-3 px-4 py-1.5 rounded-full border backdrop-blur-sm text-[12px] tracking-[0.18em] uppercase ${color}`}
    >
      <Sun size={12} className="opacity-70" />
      <span className="italic-serif normal-case tracking-normal text-[13px]">{text}</span>
      <Moon size={12} className="opacity-70" />
    </div>
  );
}

/** Manuscript-style "Today's Panchang" card. Used on the homepage as a slim section. */
export function PanchangCard() {
  const p = usePanchang();
  if (!p || !p.enabled || !p.card_enabled) return null;
  if (p.fallback || !p.tithi) {
    // Fallback: if calc failed, only show banner override if present; do not show card.
    return null;
  }

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const rows = [
    { label: "Tithi", value: p.tithi_full, Icon: Moon },
    { label: "Nakshatra", value: p.nakshatra, Icon: Star },
    { label: "Vara", value: p.vara, Icon: Sun },
    { label: "Masa", value: p.masa, Icon: Sparkles },
  ];

  return (
    <section
      data-testid="panchang-card"
      className="bg-manuscript section-tight relative overflow-hidden border-y border-[color:var(--border-strong)]"
    >
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4">
            <DecorativeDivider label="Today's Panchang" className="mb-3 justify-start" />
            <div className="flex items-baseline gap-3">
              <OmMark size={36} className="text-[color:var(--brand)]" />
              <div>
                <div className="font-heading text-3xl tracking-display leading-tight">{p.headline.split("·")[0].trim()}</div>
                <div className="text-xs italic-serif opacity-70 mt-1">{today}</div>
              </div>
            </div>
            {p.observances?.length > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[color:var(--brand)]/30 text-[color:var(--brand)] text-xs italic-serif">
                <Sparkles size={12} /> {p.observances.join(" · ")}
              </div>
            )}
          </div>

          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--border-strong)] border border-[color:var(--border-strong)] rounded-md overflow-hidden">
            {rows.map(({ label, value, Icon }) => (
              <div key={label} className="bg-[#fffdf8] p-5" data-testid={`panchang-${label.toLowerCase()}`}>
                <div className="flex items-center justify-between text-[color:var(--brand)] mb-3 opacity-70">
                  <Icon size={14} />
                  <span className="text-[9px] tracking-[0.28em] uppercase">{label}</span>
                </div>
                <div className="font-heading text-xl md:text-2xl leading-tight tracking-display">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
